import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import {
  EngineBookingAdvantagePositionType,
  EngineBookingAdvantageVariant,
} from "../../gen/witbooking/backoffice/propertyconfiguration/v1/propertyconfiguration_api_pb";
import actions from "../../lib/actions";
import CrudForm, { FormConfiguration } from "../shared/crud/form";
import { defaultCrudMessages } from "../shared/crud/simple-crud";
import { JsonEditor } from "../shared/widgets/jsonEditor";
import { EngineAppearanceEntity } from "./domain/engineAppearance.entity";
import schema from "./infrastructure/advancedEngineAppearance.schema.json";
import { getEngineAppearance, updateEngineAppearance } from "./infrastructure/engineAppearance.api";
import { engineAppearanceMessages } from "./messages";

const VARIANT_TO_STRING: Record<EngineBookingAdvantageVariant, string | null> = {
  [EngineBookingAdvantageVariant.ENGINE_BOOKING_ADVANTAGE_VARIANT_EMPTY]: null,
  [EngineBookingAdvantageVariant.COMBINED]: "combined",
  [EngineBookingAdvantageVariant.INDEPENDENT]: "independent",
};

const POSITION_TO_STRING: Record<EngineBookingAdvantagePositionType, string | null> = {
  [EngineBookingAdvantagePositionType.ENGINE_BOOKING_ADVANTAGE_POSITION_TYPE_EMPTY]: null,
  [EngineBookingAdvantagePositionType.POSITION_TOP]: "top",
  [EngineBookingAdvantagePositionType.POSITION_BOTTOM]: "bottom",
};

function toEditorForm(entity: EngineAppearanceEntity): EngineAppearanceEntity {
  return {
    ...entity,
    bookingadvantagevariant: VARIANT_TO_STRING[
      entity.bookingadvantagevariant as EngineBookingAdvantageVariant
    ] as any,
    bookingadvantageposition: POSITION_TO_STRING[
      entity.bookingadvantageposition as EngineBookingAdvantagePositionType
    ] as any,
    bookingloyaltybarposition: POSITION_TO_STRING[
      entity.bookingloyaltybarposition as EngineBookingAdvantagePositionType
    ] as any,
  };
}

export interface AdvancedEngineAppearanceEntity {
  id: string;
  serialized: any;
}

const messages = {
  ...defaultCrudMessages,
  ...engineAppearanceMessages,
};
function buildFormConfiguration(): FormConfiguration<AdvancedEngineAppearanceEntity> {
  return {
    __fieldOrder: ["serialized", "*"],
    __useProtoStyleArrays: true,
    id: {
      default: () => uuidv4(),
      hidden: true,
    },
    serialized: {
      fieldWidget: JsonEditor,
      messages: {
        label: messages.engineAppearanceJsonLabel,
        helpText: messages.engineAppearanceJsonHelpText,
      },
    },
  };
}
function AdvancedEngineAppearanceForm() {
  const intl = useIntl();
  const handleGetOne = useCallback(async () => {
    const simpleEngineAppearance = await getEngineAppearance();
    return {
      id: simpleEngineAppearance.id,
      serialized: toEditorForm(simpleEngineAppearance),
    };
  }, []);

  const handleUpdate: (entity: AdvancedEngineAppearanceEntity) => Promise<void> = useCallback(
    async entity => {
      await updateEngineAppearance(entity.serialized as EngineAppearanceEntity);
      alert(intl.formatMessage(messages.saveSuccess));
      return;
    },
    [intl]
  );
  const formConfiguration = useMemo(() => buildFormConfiguration(), []);

  return (
    <CrudForm<AdvancedEngineAppearanceEntity>
      id={"advancedEngineAppearance"}
      schema={schema}
      formConfiguration={formConfiguration}
      messages={messages}
      onGet={handleGetOne}
      onUpdate={handleUpdate}
      canUpdate={actions.ModifyAppeareanceSettings}
      enumsOptional
    />
  );
}

export default withAuthorizationVerifier(AdvancedEngineAppearanceForm);
